import { useMemo, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../ui/modal.tsx";
import { Button } from "../ui/button.tsx";
import { servicePost } from "../../api/client.ts";
import { useToast } from "../../hooks/useToast.ts";
import { useAlertDialog } from "../../hooks/useAlertDialog.tsx";
import { useAuth } from "../../auth/authContext.tsx";
import { getDbFeatureFlags } from "../../util/dbFeatureFlags.ts";
import type { TransactionOpsArgs } from "./transactionOpsModal.tsx";

/** Row context for the rental "Operations" modal — case 40. */
export interface CurrentRentalsArgs {
  transactionRowID: number;
  instantID: string;
  kioskID: number;
  productID: number;
  transactionID: number;
}

interface Props {
  args: CurrentRentalsArgs;
  onClose: () => void;
  /** Hand off to the full Transaction Operations modal (refund menu). */
  onOpenRefund: (args: TransactionOpsArgs) => void;
}

/**
 * "Operations" modal for a current rental row. Port of WebAdmin2
 * `currentRentalsPopup`: a Return action (sets the row's return date) and an
 * "Open Refund Menu" that escalates to the full Transaction Operations modal.
 * With the RentalReturnViaOperations DB flag the Return action is hidden and
 * the escalation reads "Return Operations", as WebAdmin did for NortecEU/AccessIRS.
 */
export function CurrentRentalsModal({ args, onClose, onOpenRefund }: Props) {
  const { toast } = useToast();
  const { confirm } = useAlertDialog();
  const { cache } = useAuth();
  const returnViaOps = useMemo(() => !!getDbFeatureFlags(cache).RentalReturnViaOperations, [cache]);
  const [returning, setReturning] = useState(false);

  const handleReturn = async () => {
    const ok = await confirm({
      title: "Confirm Return",
      body: <>Are you sure you wish to set <b>Row ID {args.transactionRowID}</b> as returned?</>,
      confirmLabel: "Yes — Return",
      cancelLabel: "Cancel",
      destructive: true,
    });
    if (!ok) return;
    setReturning(true);
    try {
      const result = await servicePost<boolean>("WebReturnTransactionRow", { id: args.transactionRowID });
      if (!result.bError && result.oData) {
        toast(`Transaction Row ID ${args.transactionRowID} is now returned`, "success");
        onClose();
      } else {
        toast(result.sMessage || "Could not set return. Contact Instant Systems for support.", "error");
      }
    } catch {
      toast("Could not set return. Contact Instant Systems for support.", "error");
    } finally {
      setReturning(false);
    }
  };

  const handleOpenRefund = () => {
    onOpenRefund({
      transactionID: args.transactionID,
      rowID: args.transactionRowID,
      instantID: args.instantID,
      discID: 0,
      rfid: "",
      productID: args.productID,
      productName: "",
      customerID: 0,
      kioskID: args.kioskID,
      isLocked: false,
      hasRental: false,
    });
  };

  return (
    <Modal open onClose={onClose}>
      <ModalHeader title="Operations" onClose={onClose} />
      <ModalBody>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          <div style={{ fontWeight: "var(--font-semibold)" as never }}>
            Transaction Row ID: {args.transactionRowID}
          </div>
          {!returnViaOps && (
            <Button variant="destructive" loading={returning} onClick={handleReturn}>
              Return
            </Button>
          )}
          <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-muted)" }}>
            (Refresh the search grid after a return to see it disappear from the list.)
          </div>
          <Button variant="secondary" onClick={handleOpenRefund}>
            {returnViaOps ? "Return Operations" : "Open Refund Menu"}
          </Button>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>Close</Button>
      </ModalFooter>
    </Modal>
  );
}
